import React, { useState, useContext, useEffect } from "react";
import ChildCardInfo from "./ChildCard_Info";
import { toast } from "react-toastify";
import axios from "../../utils/Axios";
import { AuthContext } from "../../context/Auth";
import Looker_Child from "./Looker_Child";

const DashboardModel = () => {
  const [isChildCard, setIsChildCard] = useState(false);
  const [childCardInfo, setChildCardInfo] = useState([]);
  const [parentModuleName, setParentModuleName] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [pathName, setPathName] = useState("");
  const [cardStatus, setCardStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedOption, setSelectedOption] = useState('katyayani');
  const { setIsEdited, isEdited } = useContext(AuthContext);
  const [debounceTimeout, setDebounceTimeout] = useState(null);
  const [isLookerChildCard, setIsLookerChildCard] = useState(false);
  const [inputs, setInputs] = useState([{ name: '', lookerUrl: '' }]);

  const addChildCardInfo = (info) => {
    setChildCardInfo((prev) => [...prev, info]);
  };

  const fetchSuggestions = async (query) => {
    try {
      const response = await axios.get(`/pbi-dashbord/moduleNameSuggestion`, {
        params: { keyword: query },
      });
      setSuggestions(response.data);
    } catch (error) {
      console.error("Error fetching module suggestions:", error);
    }
  };

  const fetchModuleInfo = async (moduleName) => {
    try {
      const response = await axios.get(`/pbi-dashbord/getSingleModuleInfo`, {
        params: { moduleName },
      });
      const { pathName, cardStatus, childCards } = response.data;

      setParentModuleName(moduleName || "");
      setPathName(pathName || "");
      setCardStatus(cardStatus || "");
      setChildCardInfo(childCards || []);
    } catch (error) {
      console.error("Error fetching module details:", error);
      toast.error("Failed to fetch module details. Please try again.");
    }
  };

  const handleParentModuleChange = (e) => {
    const value = e.target.value;
    setParentModuleName(value);

    if (debounceTimeout) {
      clearTimeout(debounceTimeout);
    }

    setDebounceTimeout(
      setTimeout(() => {
        if (value.trim()) {
          fetchSuggestions(value);
        } else {
          setSuggestions([]);
        }
      }, 300) // Debounce for 300ms
    );
  };

  const selectSuggestion = (suggestion) => {
    setParentModuleName(suggestion);
    setSuggestions([]);
    fetchModuleInfo(suggestion);
  };

  const submitData = async (e) => {
    e.preventDefault();

    const form = document.getElementById("moduleForm");
    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    if (childCardInfo.length === 0 && !cardStatus) {
      toast.error("Either add at least one child card or specify card status.");
      return;
    }

    if (selectedOption === '') {
      toast.error("Please select option from Modules Belongs to.");
      return;
    }

    const data = {
      parentModuleName,
      pathName,
      group_of: selectedOption,
      cardStatus: cardStatus.trim() || "",
      childCards: childCardInfo,
    };

    try {
      setLoading(true);
      const response = await axios.post("/pbi-dashbord/create_modules", data);

      if (response.status === 200) {
        toast.success(response.data.message);
        setIsEdited(!isEdited);
        resetForm();
      }
    } catch (error) {
      console.error("Error while adding module:", error);
      toast.error("Failed to submit data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setParentModuleName("");
    setPathName("");
    setCardStatus("");
    setChildCardInfo([]);
  };

  const deleteChildCard = (index) => {
    const confirmDelete = () => {
      setChildCardInfo((prev) => {
        const cardToDelete = prev[index];
        console.log("Deleting card at index:", index, "Card Info:", cardToDelete);
        toast.dismiss(); // Dismiss the toast after confirming
        return prev?.filter((_, i) => i !== index);
      });
    };

    toast(
      <div>
        <p>Are you sure you want to delete this card?</p>
        <div className="flex justify-end space-x-2">
          <button
            onClick={confirmDelete}
            className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
          >
            Yes
          </button>
          <button
            onClick={() => toast.dismiss()}
            className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
          >
            Cancel
          </button>
        </div>
      </div>,
      { autoClose: false, closeOnClick: false }
    );
  };

  return (
    <div className="w-full h-full overflow-y-auto p-6 bg-gray-50 space-y-6">
      {/* Header */}
      <div className="text-2xl font-bold text-gray-800">Manage Modules</div>

      {/* Module Inputs */}
      <form
        id="moduleForm"
        onSubmit={submitData}
        className="bg-white rounded-lg p-6 space-y-4"
      >
        <h3 className="text-xl font-medium text-gray-700">Add Module</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="relative">
            <label
              htmlFor="parentModuleName"
              className="block mb-2 text-sm font-medium text-gray-700"
            >
              Parent Module Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="parentModuleName"
              name="parentModuleName"
              required
              value={parentModuleName}
              onChange={handleParentModuleChange}
              placeholder="Enter Module Name"
              className="px-4 py-2 border rounded-md focus:ring focus:ring-blue-400 w-full"
            />
            {suggestions?.length > 0 && (
              <ul className="absolute z-10 bg-white border rounded-md mt-1 w-full shadow-lg max-h-40 overflow-auto">
                {suggestions.map((suggestion, index) => (
                  <li
                    key={index}
                    onClick={() => selectSuggestion(suggestion)}
                    className="px-4 py-2 hover:bg-blue-100 cursor-pointer"
                  >
                    {suggestion}
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div>
            <label
              htmlFor="path"
              className="block mb-2 text-sm font-medium text-gray-700"
            >
              Pathname
            </label>
            <input
              type="text"
              id="path"
              name="path"
              value={pathName}
              onChange={(e) => setPathName(e.target.value)}
              placeholder="Enter Pathname"
              className="px-4 py-2 border rounded-md focus:ring focus:ring-blue-400 w-full"
            />
          </div>
          <div className="w-full flex gap-4 items-center">
            <div className="W-1/2">
              <label
                htmlFor="cardStatus"
                className="block mb-2 text-sm font-medium text-gray-700"
              >
                Card Status
              </label>
              <input
                type="text"
                id="cardStatus"
                name="cardStatus"
                value={cardStatus}
                onChange={(e) => setCardStatus(e.target.value)}
                placeholder="Enter Card Status"
                className="px-4 py-2 border rounded-md focus:ring focus:ring-blue-400 w-full"
              />
            </div>
            <div className="W-1/2">
              <div>
                <label
                  htmlFor="cardStatus"
                  className="block mb-2 text-sm font-medium text-gray-700"
                >
                  Modules Belongs to <span className="font-light text-xs text-red-500">*</span>
                </label>
                <select
                  id="selectedOption"
                  name="selectedOption"
                  value={selectedOption}
                  onChange={(e) => setSelectedOption(e.target.value)}
                  className="px-4 py-2 border rounded-md focus:ring focus:ring-blue-400 w-full"
                >
                  <option value="katyayani">Katyayani</option>
                  <option value="komal">Komal</option>
                  <option value="katyayani_looker">Katyayani Looker</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        <div className="w-full flex flex-col sm:flex-row items-center gap-4">
          <button
            type="button"
            onClick={() => {
              if (selectedOption?.toLowerCase() === 'katyayani_looker') {
                setIsChildCard(false);
                setIsLookerChildCard(true);
              } else {
                setIsChildCard(true)
                setIsLookerChildCard(false);
              }
            }}
            className="flex-grow sm:flex-grow-0 px-6 py-2 bg-blue-500 text-white font-semibold rounded-md hover:bg-blue-600 transition"
          >
            Add Child Card
          </button>

          <button
            type="submit"
            className="flex-grow sm:flex-grow-0 px-6 py-2 bg-green-500 text-white font-semibold rounded-md hover:bg-green-600 transition"
          >
            {loading ? "Loading..." : "Submit"}
          </button>
        </div>
      </form>

      {/* Child Card Info */}
      {isChildCard && (
        <div className="bg-white rounded-lg p-6 mt-4">
          <h3 className="text-xl mb-4 font-medium text-gray-700">Child Card Info</h3>
          <ChildCardInfo onAdd={addChildCardInfo} />
        </div>
      )}

      {isLookerChildCard && (
        <div className="bg-white rounded-lg p-6 mt-4">
          <h3 className="text-xl mb-4 font-medium text-gray-700">Child Card Info</h3>
          <Looker_Child onAdd={addChildCardInfo} />
        </div>
      )}

      {/* Display Stored Child Card Info */}
      {childCardInfo?.length > 0 && (
        <div className="bg-white rounded-lg p-6 space-y-4 mt-4">
          <h3 className="text-xl font-semibold text-gray-700">Child Cards List</h3>
          <ul className="space-y-2">
            {childCardInfo?.map((info, index) => (
              <li
                key={info.id || index}
                className="flex justify-between items-center bg-gray-100 p-4 rounded-md"
              >
                <div>
                  <strong>Child Name:</strong> {info.childCardName || info.name},
                  {selectedOption === 'katyayani_looker' ? (
                    <>
                      <strong>URL:</strong> {info.url_looker}
                    </>
                  ) : (
                    <>
                      <strong>Report ID:</strong> {info.reportId},
                      <strong>Group ID:</strong> {info.groupId}
                    </>
                  )}
                </div>
                <button
                  onClick={() => deleteChildCard(index)}
                  className="text-red-500 hidden hover:text-red-700 transition"
                >
                  Delete
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default DashboardModel;
